import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, DollarSign, FileText, Download, Wallet, Clock, PieChart, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/currency";
import { toast } from "sonner";
import { format, parseISO, isValid } from "date-fns";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
const CHART_COLORS = {
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  sublimation: '#3b82f6',
  embroidery: '#8b5cf6'
};

// Custom tooltip for currency
const CurrencyTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg">
        <p className="font-medium text-sm">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Reports() {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  const [dailySales, setDailySales] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);

  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    enabled: false,
    tax_name: "VAT",
    tax_rate: 0,
    tax_id: "",
    include_in_price: false
  });
  
  // Payment Activity Period Filter
  const [activityPeriod, setActivityPeriod] = useState("daily");

  // Safe date formatting
  const safeFormatDate = (dateString, formatStr = "MMM dd, yyyy") => {
    if (!dateString) return "N/A";
    try {
      const parsed = parseISO(dateString);
      return isValid(parsed) ? format(parsed, formatStr) : "N/A";
    } catch {
      return "N/A";
    }
  };

  useEffect(() => {
    fetchReportData();
    fetchPaymentSummary();
    // Set current month
    const currentMonth = new Date().toISOString().slice(0, 7);
    setSelectedMonth(currentMonth);
    // Set current date
    const currentDate = new Date().toISOString().slice(0, 10);
    setSelectedDate(currentDate);
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthlyTransactions(selectedMonth);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedDate) {
      fetchDailySales(selectedDate);
    }
  }, [selectedDate]);

  const fetchReportData = async () => {
    try {
      const response = await axios.get(`${API}/reports/sales`);
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyTransactions = async (month) => {
    try {
      const response = await axios.get(`${API}/reports/monthly-transactions?month=${month}`);
      setMonthlyTransactions(response.data.transactions);
    } catch (error) {
      console.error("Error fetching monthly transactions:", error);
    }
  };

  const fetchDailySales = async (date) => {
    try {
      const response = await axios.get(`${API}/reports/daily-sales?date=${date}`);
      setDailySales(response.data);
    } catch (error) {
      console.error("Error fetching daily sales:", error);
    }
  };

  const fetchPaymentSummary = async (period = "daily") => {
    try {
      const response = await axios.get(`${API}/reports/payment-summary?period=${period}`);
      setPaymentSummary(response.data);
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    }
  };

  // Delete order function
  const handleDeleteOrder = async (orderId, clientName) => {
    if (!orderId) {
      toast.error("Cannot delete: Order ID not found");
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the order for "${clientName}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted successfully");
      
      // Refresh the data
      fetchReportData();
      if (selectedMonth) fetchMonthlyTransactions(selectedMonth);
      if (selectedDate) fetchDailySales(selectedDate);
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };
  
  // Refetch when period changes
  useEffect(() => {
    if (activityPeriod) {
      fetchPaymentSummary(activityPeriod);
    }
  }, [activityPeriod]);

  const exportToCSV = (data, filename) => {
    // Convert data to CSV format
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(`${filename} exported successfully!`);
  };

  const exportMonthlyReport = () => {
    if (monthlyTransactions.length === 0) {
      toast.error("No data to export");
      return;
    }
    exportToCSV(monthlyTransactions, `Monthly-Report-${selectedMonth}`);
  };

  const exportDailyReport = () => {
    if (!dailySales || dailySales.transactions.length === 0) {
      toast.error("No data to export");
      return;
    }
    exportToCSV(dailySales.transactions, `Daily-Sales-${selectedDate}`);
  };

  const exportSummaryReport = () => {
    if (!reportData) {
      toast.error("No data to export");
      return;
    }
    
    const summaryData = [
      {
        metric: 'Total Sales',
        value: reportData.summary.total_sales
      },
      {
        metric: 'Total Paid',
        value: reportData.summary.total_paid
      },
      {
        metric: 'Outstanding',
        value: reportData.summary.total_outstanding
      },
      {
        metric: 'Transactions',
        value: reportData.summary.total_transactions
      }
    ];
    
    exportToCSV(summaryData, 'Sales-Summary-Report');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  const { summary, weekly, monthly } = reportData;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-heading font-extrabold tracking-tight text-primary">
                Sales Reports
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Analytics and insights</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard">
            Back to Dashboard
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Sales</p>
                  <p className="text-2xl font-heading font-bold text-green-600">
                    {formatCurrency(summary.total_sales)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Paid</p>
                  <p className="text-2xl font-heading font-bold text-blue-600">
                    {formatCurrency(summary.total_paid)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Outstanding</p>
                  <p className="text-2xl font-heading font-bold text-amber-600">
                    {formatCurrency(summary.total_outstanding)}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-amber-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Transactions</p>
                  <p className="text-2xl font-heading font-bold">
                    {summary.total_transactions}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-4 flex justify-end">
          <Button onClick={exportSummaryReport} variant="outline" data-testid="export-summary">
            <Download className="h-4 w-4 mr-2" />
            Export Summary
          </Button>
        </div>

        {/* Reports Tabs */}
        <Card>
          <CardHeader>
            <CardTitle>Detailed Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="charts">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="charts" data-testid="charts-tab">
                  <PieChart className="h-4 w-4 mr-1" />
                  Charts
                </TabsTrigger>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="daily">Daily Sales</TabsTrigger>
                <TabsTrigger value="payments" data-testid="payment-summary-tab">Payments</TabsTrigger>
                <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
              </TabsList>

              {/* Charts Tab */}
              <TabsContent value="charts" className="mt-6" data-testid="charts-content">
                <div className="space-y-8">
                  {/* Daily Orders Count */}
                  <div>
                    <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Daily Total Number of Orders
                    </h3>
                    <Card>
                      <CardContent className="p-6">
                        {!reportData?.daily || Object.keys(reportData.daily).length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No daily data available</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                              data={Object.entries(reportData.daily)
                                .sort((a, b) => a[0].localeCompare(b[0]))
                                .slice(-14)
                                .map(([date, data]) => ({
                                  date: format(parseISO(date), 'MMM d'),
                                  orders: data.order_count,
                                  quantity: data.total_quantity
                                }))}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip 
                                formatter={(value, name) => [value, name === 'orders' ? 'Orders' : 'Total Qty']}
                                labelFormatter={(label) => `Date: ${label}`}
                              />
                              <Legend />
                              <Bar dataKey="orders" name="Orders" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                              <Bar dataKey="quantity" name="Total Qty" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Daily Orders Input (New Orders Created) */}
                    <div>
                      <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        Daily Orders Input
                      </h3>
                      <Card>
                        <CardContent className="p-6">
                          {!reportData?.daily || Object.keys(reportData.daily).length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">No data available</p>
                          ) : (
                            <ResponsiveContainer width="100%" height={250}>
                              <AreaChart
                                data={Object.entries(reportData.daily)
                                  .sort((a, b) => a[0].localeCompare(b[0]))
                                  .slice(-14)
                                  .map(([date, data]) => ({
                                    date: format(parseISO(date), 'MMM d'),
                                    orders: data.order_count
                                  }))}
                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                              >
                                <defs>
                                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                                <Tooltip formatter={(value) => [`${value} orders`, 'New Orders']} />
                                <Area 
                                  type="monotone" 
                                  dataKey="orders" 
                                  stroke={CHART_COLORS.primary} 
                                  fillOpacity={1} 
                                  fill="url(#colorOrders)" 
                                  name="Orders Created"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Daily Sales Received (Payments) */}
                    <div>
                      <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-primary" />
                        Daily Sales Received
                      </h3>
                      <Card>
                        <CardContent className="p-6">
                          {!reportData?.daily || Object.keys(reportData.daily).length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">No data available</p>
                          ) : (
                            <ResponsiveContainer width="100%" height={250}>
                              <AreaChart
                                data={Object.entries(reportData.daily)
                                  .sort((a, b) => a[0].localeCompare(b[0]))
                                  .slice(-14)
                                  .map(([date, data]) => ({
                                    date: format(parseISO(date), 'MMM d'),
                                    sales: data.total_sales,
                                    received: data.payments_received
                                  }))}
                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                              >
                                <defs>
                                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₱${(v/1000).toFixed(0)}k`} />
                                <Tooltip content={<CurrencyTooltip />} />
                                <Legend />
                                <Area 
                                  type="monotone" 
                                  dataKey="received" 
                                  stroke={CHART_COLORS.success} 
                                  fillOpacity={1} 
                                  fill="url(#colorSales)" 
                                  name="Payments Received"
                                />
                                <Line type="monotone" dataKey="sales" stroke={CHART_COLORS.primary} name="Total Sales" strokeWidth={2} />
                              </AreaChart>
                            </ResponsiveContainer>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Weekly Trend */}
                  <div>
                    <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Weekly Trend
                    </h3>
                    <Card>
                      <CardContent className="p-6">
                        {Object.keys(weekly).length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No weekly data available</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={Object.entries(weekly)
                                .sort((a, b) => a[0].localeCompare(b[0]))
                                .slice(-8)
                                .map(([week, data]) => ({
                                  week: `Week ${week.split('-W')[1]}`,
                                  sales: data.total_sales,
                                  paid: data.total_paid,
                                  orders: data.transaction_count
                                }))}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                              <YAxis yAxisId="left" tickFormatter={(v) => `₱${(v/1000).toFixed(0)}k`} />
                              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                              <Tooltip content={<CurrencyTooltip />} />
                              <Legend />
                              <Bar yAxisId="left" dataKey="sales" name="Total Sales" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                              <Bar yAxisId="left" dataKey="paid" name="Payments Received" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
                              <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders Count" stroke={CHART_COLORS.warning} strokeWidth={3} dot={{ r: 4 }} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-6">
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold mb-4">Monthly Summary</h3>
                  {Object.keys(monthly).length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No monthly data available</p>
                  ) : (
                    Object.entries(monthly)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .map(([month, data]) => (
                        <div key={month} className="flex items-center justify-between p-4 border border-border rounded-md">
                          <div>
                            <p className="font-semibold">{month}</p>
                            <p className="text-sm text-muted-foreground">{data.transaction_count} transactions</p>
                          </div>
                          <p className="text-lg font-heading font-bold text-green-600">
                            {formatCurrency(data.total_sales)}
                          </p>
                        </div>
                      ))
                  )}
                </div>
              </TabsContent>

              {/* Monthly Transactions Tab */}
              <TabsContent value="monthly" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Label>Select Month:</Label>
                      <Input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="w-48"
                        data-testid="month-selector"
                      />
                    </div>
                    <Button onClick={exportMonthlyReport} variant="outline" data-testid="export-monthly">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>

                  {monthlyTransactions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No transactions for this month</p>
                  ) : (
                    <div className="border border-border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Client</th>
                            <th className="text-left p-3">Product</th>
                            <th className="text-left p-3">Method</th>
                            <th className="text-right p-3">Amount</th>
                            <th className="text-right p-3">Paid</th>
                            <th className="text-right p-3">Balance</th>
                            <th className="text-center p-3">Status</th>
                            <th className="text-left p-3">Mode of Payment</th>
                            <th className="text-center p-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyTransactions.map((transaction, index) => (
                            <tr key={index} className="border-t border-border hover:bg-secondary/50">
                              <td className="p-3">{transaction.date}</td>
                              <td className="p-3 font-medium">{transaction.client_name}</td>
                              <td className="p-3">{transaction.product_type}</td>
                              <td className="p-3">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  transaction.printing_method === 'Sublimation' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {transaction.printing_method}
                                </span>
                              </td>
                              <td className="p-3 text-right font-semibold">{formatCurrency(transaction.amount)}</td>
                              <td className="p-3 text-right text-blue-600">{formatCurrency(transaction.down_payment)}</td>
                              <td className="p-3 text-right text-amber-600">{formatCurrency(transaction.balance)}</td>
                              <td className="p-3 text-center">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  transaction.payment_status === 'Paid' 
                                    ? 'bg-green-100 text-green-700' 
                                    : transaction.payment_status === 'Partial'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {transaction.payment_status}
                                </span>
                              </td>
                              <td className="p-3 text-left">{transaction.mode_of_payment || '-'}</td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteOrder(transaction.id, transaction.client_name)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-monthly-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-secondary font-semibold">
                          <tr>
                            <td colSpan="4" className="p-3 text-right">TOTAL:</td>
                            <td className="p-3 text-right">
                              {formatCurrency(monthlyTransactions.reduce((sum, t) => sum + t.amount, 0))}
                            </td>
                            <td className="p-3 text-right text-blue-600">
                              {formatCurrency(monthlyTransactions.reduce((sum, t) => sum + t.down_payment, 0))}
                            </td>
                            <td className="p-3 text-right text-amber-600">
                              {formatCurrency(monthlyTransactions.reduce((sum, t) => sum + t.balance, 0))}
                            </td>
                            <td colSpan="3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Daily Sales Tab */}
              <TabsContent value="daily" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Label>Select Date:</Label>
                      <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-48"
                        data-testid="date-selector"
                      />
                    </div>
                    <Button onClick={exportDailyReport} variant="outline" data-testid="export-daily">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>

                  {dailySales && (
                    <>
                      {/* Daily Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-sm text-muted-foreground mb-1">Daily Sales</p>
                            <p className="text-xl font-heading font-bold text-green-600">
                              {formatCurrency(dailySales.summary.total_sales)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-sm text-muted-foreground mb-1">Transactions</p>
                            <p className="text-xl font-heading font-bold">
                              {dailySales.summary.transaction_count}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-sm text-muted-foreground mb-1">Outstanding</p>
                            <p className="text-xl font-heading font-bold text-amber-600">
                              {formatCurrency(dailySales.summary.total_outstanding)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Daily Transactions */}
                      {dailySales.transactions.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No transactions for this date</p>
                      ) : (
                        <div className="border border-border rounded-md overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-secondary">
                              <tr>
                                <th className="text-left p-3">#</th>
                                <th className="text-left p-3">Client</th>
                                <th className="text-left p-3">Product</th>
                                <th className="text-left p-3">Method</th>
                                <th className="text-center p-3">Qty</th>
                                <th className="text-right p-3">Amount</th>
                                <th className="text-center p-3">Payment Status</th>
                                <th className="text-left p-3">Mode of Payment</th>
                                <th className="text-center p-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailySales.transactions.map((transaction, index) => (
                                <tr key={index} className="border-t border-border hover:bg-secondary/50">
                                  <td className="p-3">{index + 1}</td>
                                  <td className="p-3 font-medium">{transaction.client_name}</td>
                                  <td className="p-3">{transaction.product_type}</td>
                                  <td className="p-3">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      transaction.printing_method === 'Sublimation' 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {transaction.printing_method}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className="bg-primary/10 text-primary font-semibold px-2 py-1 rounded">
                                      {transaction.quantity || 0}
                                    </span>
                                  </td>
                                  <td className="p-3 text-right font-semibold">{formatCurrency(transaction.amount)}</td>
                                  <td className="p-3 text-center">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      transaction.payment_status === 'Paid' 
                                        ? 'bg-green-100 text-green-700' 
                                        : transaction.payment_status === 'Partial'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                      {transaction.payment_status}
                                    </span>
                                  </td>
                                  <td className="p-3 text-left">{transaction.mode_of_payment || '-'}</td>
                                  <td className="p-3 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteOrder(transaction.id, transaction.client_name)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-daily-${index}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-secondary font-semibold">
                              <tr>
                                <td colSpan="4" className="p-3 text-right">TOTAL:</td>
                                <td className="p-3 text-center">
                                  {dailySales.summary.total_quantity || dailySales.transactions.reduce((sum, t) => sum + (t.quantity || 0), 0)}
                                </td>
                                <td className="p-3 text-right">
                                  {formatCurrency(dailySales.transactions.reduce((sum, t) => sum + t.amount, 0))}
                                </td>
                                <td colSpan="3"></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Payment Summary Tab */}
              <TabsContent value="payments" className="mt-6" data-testid="payment-summary-content">
                {paymentSummary ? (
                  <div className="space-y-6">
                    {/* Payment Totals */}
                    <div>
                      <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                        <Wallet className="h-5 w-5 text-primary" />
                        Payment Overview
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Total Order Value</p>
                            <p className="text-lg font-heading font-bold">
                              {formatCurrency(paymentSummary.totals.total_order_value)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Down Payments</p>
                            <p className="text-lg font-heading font-bold text-blue-600">
                              {formatCurrency(paymentSummary.totals.total_down_payments)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Balance Payments</p>
                            <p className="text-lg font-heading font-bold text-indigo-600">
                              {formatCurrency(paymentSummary.totals.total_balance_payments)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Total Collected</p>
                            <p className="text-lg font-heading font-bold text-green-600">
                              {formatCurrency(paymentSummary.totals.total_collected)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
                            <p className="text-lg font-heading font-bold text-amber-600">
                              {formatCurrency(paymentSummary.totals.total_outstanding)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground mb-1">Collection Rate</p>
                            <p className="text-lg font-heading font-bold text-primary">
                              {paymentSummary.totals.collection_rate}%
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    {/* Payment Activity with Period Filter */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold flex items-center gap-2">
                          <Clock className="h-5 w-5 text-primary" />
                          Payment Activity
                        </h3>
                        <div className="flex items-center gap-2">
                          <Select value={activityPeriod} onValueChange={setActivityPeriod}>
                            <SelectTrigger className="w-32" data-testid="activity-period-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Period Summary */}
                      {paymentSummary.period_summary && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                            <CardContent className="p-4">
                              <p className="text-xs text-green-700 mb-1">
                                {activityPeriod === "daily" ? "Today's" : activityPeriod === "weekly" ? "This Week's" : "This Month's"} Collections
                              </p>
                              <p className="text-2xl font-heading font-bold text-green-600">
                                {formatCurrency(paymentSummary.period_summary.total_amount)}
                              </p>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                            <CardContent className="p-4">
                              <p className="text-xs text-blue-700 mb-1">Transactions</p>
                              <p className="text-2xl font-heading font-bold text-blue-600">
                                {paymentSummary.period_summary.payment_count}
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {paymentSummary.recent_payments.length === 0 ? (
                        <Card>
                          <CardContent className="p-8 text-center text-muted-foreground">
                            No payment activity for this period
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="border border-border rounded-md overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-secondary">
                              <tr>
                                <th className="text-left p-3">Type</th>
                                <th className="text-right p-3">Amount</th>
                                <th className="text-left p-3">Payment Date</th>
                                <th className="text-right p-3">Balance After</th>
                                <th className="text-left p-3">Mode</th>
                                <th className="text-left p-3">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentSummary.recent_payments.map((payment, index) => (
                                <tr key={index} className="border-t border-border hover:bg-secondary/50">
                                  <td className="p-3">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      payment.type === "Down Payment" 
                                        ? "bg-blue-100 text-blue-800" 
                                        : "bg-green-100 text-green-800"
                                    }`}>
                                      {payment.type}
                                    </span>
                                  </td>
                                  <td className="p-3 text-right font-semibold text-green-600">
                                    +{formatCurrency(payment.amount)}
                                  </td>
                                  <td className="p-3">{safeFormatDate(payment.payment_date)}</td>
                                  <td className="p-3 text-right font-medium">
                                    {formatCurrency(payment.balance_after)}
                                  </td>
                                  <td className="p-3">{payment.mode_of_payment || "-"}</td>
                                  <td className="p-3 text-muted-foreground text-xs">{payment.notes || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading payment summary...</p>
                  </div>
                )}
              </TabsContent>

              {/* Breakdown Tab */}
              <TabsContent value="breakdown" className="mt-6">
                <div className="space-y-4">
                  <h3 className="font-heading font-semibold mb-4">Weekly Breakdown</h3>
                  {Object.keys(weekly).length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No weekly data available</p>
                  ) : (
                    Object.entries(weekly)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .slice(0, 12)
                      .map(([week, data]) => (
                        <div key={week} className="flex items-center justify-between p-4 border border-border rounded-md">
                          <div>
                            <p className="font-semibold">Week {week.split('-W')[1]}, {week.split('-')[0]}</p>
                            <p className="text-sm text-muted-foreground">{data.transaction_count} transactions</p>
                          </div>
                          <p className="text-lg font-heading font-bold text-green-600">
                            {formatCurrency(data.total_sales)}
                          </p>
                        </div>
                      ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
